import type { AnchorHTMLAttributes, ReactNode } from 'react';
import React from 'react';
import type { Theme } from '@material-ui/core';
import { Icon } from '@material-ui/core';
import classnames from 'classnames';
import { Link } from '../Link';

import { useKlpStyles } from '../Theme/useKlpStyles';
import type {
    CommonProps,
    OverridableTypeMap,
} from '@material-ui/core/OverridableComponent';

import type { WithTranslation } from '../TranslationOrChildren';
import type { KlpColor, KlpStyled } from '../Theme';
import { Block } from '../Block';

export enum ActionLinkType {
    link = 'link',
    linkBack = 'linkBack',
}

export type ActionLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> &
    CommonProps<OverridableTypeMap> &
    WithTranslation &
    KlpStyled & {
        actionType: keyof typeof ActionLinkType;
        target?: string;
        startIcon?: React.ReactNode;
        endIcon?: React.ReactNode;
        disabled?: boolean;
        color?: KlpColor;
    };

const styles = (theme: Theme) => ({
    iconWrapper: {
        display: 'flex',
        alignItems: 'center',
    },
    iconStart: {
        cursor: 'pointer',
        marginRight: theme.spacing(0.5),
    },
    iconEnd: {
        cursor: 'pointer',
        marginLeft: theme.spacing(0.5),
    },
    linkDisabled: {
        color: theme.palette.primary.medium,
        '&:before': {
            backgroundColor: theme.palette.primary.medium,
        },
        '&:hover:before': {
            backgroundColor: theme.palette.primary.medium,
        },
    },
});

const iconMapStart: Partial<Record<ActionLinkType, string>> = {
    linkBack: 'arrow_back',
};

const isReactNode = (node): node is ReactNode => React.isValidElement(node);

export const ActionLink: React.FC<ActionLinkProps> = (props) => {
    const {
        className,
        actionType,
        translation,
        translationOptions,
        disabled,
        startIcon,
        endIcon,
        color: _color,
        onClick,
        ...rest
    } = props;

    const classes = useKlpStyles(props, styles);

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault();
        onClick?.(event);
    };

    const getIcon = (
        icon: ReactNode | undefined,
        type: keyof typeof ActionLinkType,
        position: 'start' | 'end',
    ) => {
        if (isReactNode(icon)) {
            return (
                <Block
                    className={classnames(
                        classes.iconWrapper,
                        position === 'start'
                            ? classes.iconStart
                            : classes.iconEnd,
                    )}
                >
                    {icon}
                </Block>
            );
        }

        if (position === 'start' && iconMapStart[type]) {
            return (
                <Icon className={classes.iconStart} fontSize="inherit">
                    {iconMapStart[type]}
                </Icon>
            );
        }
        return null;
    };

    return (
        <>
            <Link
                startIcon={getIcon(startIcon, actionType, 'start')}
                endIcon={getIcon(endIcon, actionType, 'end')}
                onClick={!disabled ? handleClick : undefined}
                translation={translation}
                translationOptions={translationOptions}
                className={classnames(classes.klpStyles, className, {
                    [classes.linkDisabled]: disabled,
                })}
                disabled={disabled}
                {...rest}
            />
        </>
    );
};
